type Listener = () => void;

const listeners = new Set<Listener>();
let activated = false;

/**
 * Pinned scroll scenes are built as each section is approached, and every pin
 * adds thousands of pixels of spacing to the document. That is the right
 * trade-off while the visitor scrolls, and the wrong one the moment something
 * aims at a section: the target moves under the scroll as the scenes below it
 * materialise. Anything that jumps rather than scrolls asks for them all up
 * front, once.
 */
export function activateScrollScenes(): void {
  if (activated) {
    return;
  }

  activated = true;
  listeners.forEach((listener) => listener());
  listeners.clear();
}

export function scrollScenesActivated(): boolean {
  return activated;
}

/**
 * Focus scrolling is the browser's, not ours: pressing Tab onto a control below
 * the fold triggers one native `scrollIntoView` that never re-aims. If the
 * pinned scenes are still unbuilt at that moment it lands thousands of pixels
 * short and the focus ring ends up somewhere the visitor cannot see. The first
 * Tab is the earliest honest signal that keyboard navigation is in use, and
 * every control above the fold comes first, so the scenes are ready by the time
 * focus reaches one that needs them.
 */
export function watchKeyboardScrollIntent(): void {
  const onKeyDown = (event: KeyboardEvent) => {
    if (event.key === 'Tab') {
      window.removeEventListener('keydown', onKeyDown);
      activateScrollScenes();
    }
  };

  window.addEventListener('keydown', onKeyDown);
}

/** Returns an unsubscribe function, so React effects can clean up. */
export function onScrollScenesActivated(listener: Listener): () => void {
  if (activated) {
    listener();
    return () => undefined;
  }

  listeners.add(listener);
  return () => listeners.delete(listener);
}
